import { useState, useEffect } from 'react';
import api from '../services/api';
import DataTable from '../components/DataTable';
import BrandForm from '../components/BrandForm';
import BrandPresetSelector from '../components/BrandPresetSelector';
import { useToast } from '../hooks/useToast';

export default function Brands() {
  const [brands, setBrands] = useState([]);
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showPresetSelector, setShowPresetSelector] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { showToast } = useToast();

  useEffect(() => {
    fetchBrands();
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brands, searchTerm]);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/filament-brands');
      setBrands(response.data.data);
    } catch (err) {
      setError('Failed to fetch brands');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...brands];

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(b =>
        b.name?.toLowerCase().includes(search) ||
        b.key?.toLowerCase().includes(search) ||
        b.websiteUrl?.toLowerCase().includes(search)
      );
    }

    setFilteredBrands(filtered);
  };

  const handleAdd = () => {
    setEditingBrand(null);
    setShowPresetSelector(true);
  };

  const handleSelectPreset = (duplicatedBrand) => {
    setShowPresetSelector(false);
    setEditingBrand(duplicatedBrand);
    setShowForm(true);
    showToast('Brand created from preset. You can now customize it.', 'success');
  };

  const handleCreateFromScratch = () => {
    setShowPresetSelector(false);
    setEditingBrand(null);
    setShowForm(true);
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    setShowForm(true);
  };

  const handleDelete = async (brand) => {
    if (!window.confirm(`Are you sure you want to delete "${brand.name}"?`)) {
      return;
    }

    try {
      await api.delete(`/auth/filament-brands/${brand._id}`);
      fetchBrands();
    } catch (err) {
      alert('Failed to delete brand');
      console.error(err);
    }
  };

  const handleDuplicate = async (brand) => {
    try {
      const response = await api.post(`/auth/filament-brands/${brand._id}/duplicate?from=farm`);
      setEditingBrand(response.data.data);
      setShowForm(true);
      showToast('Brand duplicated. You can now customize it.', 'success');
    } catch (err) {
      alert('Failed to duplicate brand');
      console.error(err);
    }
  };

  const handleSave = async (data) => {
    const isEditing = !!editingBrand;
    if (isEditing) {
      await api.patch(`/auth/filament-brands/${editingBrand._id}`, data);
    } else {
      await api.post('/auth/filament-brands', data);
    }
    setShowForm(false);
    setEditingBrand(null);
    fetchBrands();
    showToast(isEditing ? 'Brand updated successfully!' : 'Brand created successfully!', 'success');
  };

  const columns = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'key', label: 'Key', sortable: true },
    {
      key: 'websiteUrl',
      label: 'Website',
      render: (val) => val ? (
        <a
          href={val}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline"
        >
          {val}
        </a>
      ) : 'N/A'
    },
    {
      key: 'logoUrl',
      label: 'Logo',
      render: (val) => val ? (
        <img
          src={val}
          alt="Brand logo"
          className="h-8 w-auto object-contain"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      ) : 'N/A'
    },
    {
      key: 'showInSite',
      label: 'Show in Site',
      render: (val) => (
        <span className={`px-2 py-1 text-xs font-medium rounded ${val ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}>
          {val ? 'Yes' : 'No'}
        </span>
      )
    }
  ];

  const renderFilters = () => (
    <div className="filters">
      <input
        type="text"
        placeholder="Search by name, key, or website..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
    </div>
  );

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div>
      {showPresetSelector ? (
        <BrandPresetSelector
          onSelectPreset={handleSelectPreset}
          onCreateNew={handleCreateFromScratch}
          onCancel={() => setShowPresetSelector(false)}
        />
      ) : showForm ? (
        <BrandForm
          brand={editingBrand}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingBrand(null);
          }}
        />
      ) : (
        <DataTable
          data={filteredBrands}
          columns={columns}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
          onAdd={handleAdd}
          renderFilters={renderFilters}
        />
      )}
    </div>
  );
}
